/**
 * Errors signalling that the LLM's tool input was structurally valid, but
 * the data on the input dataset doesn't match the operation's assumptions.
 *
 * Recovery strategy: the LLM should re-inspect the dataset (sample rows,
 * fetch schema, examine cardinality) before retrying with a different
 * approach.
 */

import { encode as toonEncode } from '@toon-format/toon';

import { type ErrorRecoveryStrategy, ToolExecutionError } from './base.ts';
import { toolError } from './registry.ts';

@toolError('DataShapeError')
export abstract class DataShapeError extends ToolExecutionError {
  readonly recovery: ErrorRecoveryStrategy = 'inspect-data-then-retry';
}

/**
 * An operation expected a unique row per group / per series but found
 * multiple. Raised by `expandTimeseries` (multiple rows for the same
 * splitBy value) and by `pivot` (key column has more unique values than
 * the pivot can fan out to).
 */
@toolError('AmbiguousRowsError')
export class AmbiguousRowsError extends DataShapeError {
  constructor(
    readonly operation: string,
    message: string,
    readonly groupingKey: string,
    readonly conflictCount: number,
  ) {
    super(message);
  }

  override suggestions(): readonly string[] {
    return [
      `Aggregate or filter by "${this.groupingKey}" so each group resolves to a single row before calling ${this.operation}.`,
      'Use a `groupBy` action upstream to collapse duplicate rows.',
    ];
  }
}

/**
 * A column expected to hold unique values across rows contained duplicates.
 * Raised by `expandTimeseries` when the splitBy column has duplicate values.
 *
 * Values are kept in their original shape (objects, primitives) and rendered
 * with TOON encoding so the LLM sees structured content rather than
 * `[object Object]` for ref-shaped columns. Capped at the first 5 duplicates
 * to keep the error message bounded.
 */
@toolError('DuplicateKeyError')
export class DuplicateKeyError extends DataShapeError {
  constructor(
    readonly column: string,
    readonly duplicateValues: readonly unknown[],
  ) {
    super(`Column "${column}" has duplicate values:\n${formatDuplicateValues(duplicateValues)}`);
  }

  override suggestions(): readonly string[] {
    return [
      `Aggregate by "${this.column}" upstream so each value appears at most once.`,
      'Use a different column with unique values for the split.',
    ];
  }
}

/**
 * Render up to 5 duplicate values as TOON. `toon-format/toon`'s `encode`
 * picks the right shape automatically: homogeneous object arrays render
 * tabularly (`[N]{externalId,space}: A,x / B,x`); primitive arrays render
 * inline (`[N]: A,B,C`); heterogeneous arrays fall back to the dash list
 * form. Far more useful for the LLM than `[object Object]`.
 */
function formatDuplicateValues(values: readonly unknown[]): string {
  return toonEncode(values.slice(0, 5));
}

/**
 * A column expected to hold non-null values contained nulls. Raised by
 * `expandTimeseries` when the splitBy column has null entries.
 */
@toolError('NullValueError')
export class NullValueError extends DataShapeError {
  constructor(
    readonly column: string,
    readonly nullCount: number,
  ) {
    super(`Column "${column}" contains ${nullCount} null value${nullCount === 1 ? '' : 's'}.`);
  }

  override suggestions(): readonly string[] {
    return [
      `Filter out rows where "${this.column}" is null upstream.`,
      `Coalesce "${this.column}" to a non-null fallback before calling this operation.`,
    ];
  }
}

/**
 * A partition invariant assumed by the operator was violated by the data.
 * Raised by knowledge-graph queries when a partition key produces overlapping
 * or empty groups that the query expected to be disjoint and non-empty.
 */
@toolError('PartitionInvariantError')
export class PartitionInvariantError extends DataShapeError {
  constructor(
    message: string,
    readonly partitionKey: string,
  ) {
    super(message);
  }

  override suggestions(): readonly string[] {
    return [
      `Inspect the values of "${this.partitionKey}" — the partition is not behaving as expected.`,
      'Verify the upstream query produced the expected number of groups.',
    ];
  }
}

/**
 * The `spread` / `unroll` / `expandTimeseries` operators received a column
 * whose cells are CDF datapoint arrays — these need `expandTimeseries` (which
 * understands the timestamp/value pair shape), not the generic spread/unroll.
 *
 * Raised by `arquero-executor` (spread, unroll) and by `expand-timeseries`
 * (when the input column doesn't actually contain datapoint arrays).
 */
@toolError('DatapointArrayShapeError')
export class DatapointArrayShapeError extends DataShapeError {
  constructor(
    readonly operation: string,
    readonly column: string,
    /** True when the column IS a datapoint array but the wrong operator was used; false when the column is NOT a datapoint array but `expandTimeseries` was called. */
    readonly isDatapointArray: boolean,
  ) {
    super(
      isDatapointArray
        ? `${operation}: column "${column}" contains CDF datapoint arrays — use expandTimeseries instead.`
        : `${operation}: column "${column}" is not a CDF datapoint array.`,
    );
  }

  override suggestions(): readonly string[] {
    if (this.isDatapointArray) {
      return [
        `Use the expandTimeseries action on "${this.column}" — it understands the timestamp/value pair shape and produces wide-format chart-ready output.`,
        'spread/unroll only work on plain scalar arrays.',
      ];
    }
    return [
      `Verify column "${this.column}" comes from fetchTimeseriesDatapoints or another producer of datapoint arrays.`,
      'expandTimeseries requires a column whose cells are arrays of {timestamp, value} pairs.',
    ];
  }
}
